import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { Navigate, useNavigate } from 'react-router-dom';
import AuthService from '../services/AuthService';
import * as StoryScript from '../services/StoryScriptService';
import { surface } from '../theme';

const FIELDS = [
  {
    key: 'storyBible',
    label: 'Story bible',
    hint: 'Niche rules sent with every planning and chapter turn. Leave empty to send nothing.',
  },
  {
    key: 'plotsPrompt',
    label: 'Plots prompt',
    hint: 'Used to generate the plot. Supports {TITLE} and {CONTEXT}.',
  },
  {
    key: 'synopsisPrompt',
    label: 'Synopsis prompt',
    hint: 'Used after a plot is selected. Supports {TITLE}, {PLOT}, and {CONTEXT}.',
  },
  {
    key: 'outlinePrompt',
    label: 'Outline prompt',
    hint: 'Use {CHAPTER_COUNT} for the count and {CHAPTER_ACT_STRUCTURE} for chapter headings across three acts.',
  },
  { key: 'characterDescriptionPrompt', label: 'Character descriptions prompt' },
  {
    key: 'styleGuidePrompt',
    label: 'NovelCrafter-style guide',
    hint: 'Included as chapter-writing style context.',
  },
  {
    key: 'storyGenrePrompt',
    label: 'NovelCrafter-style story genre',
    hint: 'Included as chapter-writing genre context.',
  },
  {
    key: 'chapterSystemPromptTemplate',
    label: 'NovelCrafter-style system prompt',
    hint: 'System message for chapter generation. The codex is appended automatically.',
  },
  {
    key: 'chapterStorySoFarPromptTemplate',
    label: 'NovelCrafter-style previous chapter prompt',
    hint: 'Supports {storySoFar}. During parallel generation, this receives the previous chapter outline.',
  },
  {
    key: 'chapterInstructionPromptTemplate',
    label: 'NovelCrafter-style instruction prompt',
    hint: 'Supports {pov}, {message}, and {storyNotes}. {message} is the current scene beat. {storyNotes} is the story context.',
  },
];

const CHAPTER_POINT_OF_VIEW_OPTIONS = [
  { value: 'third-person', label: 'Third person' },
  { value: 'first-person', label: 'First person' },
];

const CHAPTER_STORY_SO_FAR_OPTIONS = [
  { value: 'previous-chapter', label: 'Previous chapter only' },
  { value: 'full-story', label: 'Full story so far' },
];

const emptyPromptSystem = FIELDS.reduce((acc, { key }) => ({ ...acc, [key]: '' }), {});

function normalizePromptSystem(item) {
  return {
    ...emptyPromptSystem,
    label: '',
    chapterPointOfView: 'third-person',
    chapterCount: 24,
    chapterStorySoFar: 'previous-chapter',
    minChapterWords: 0,
    ...(item || {}),
  };
}

export default function AiStoriesPromptSystemEditPage() {
  const navigate = useNavigate();
  const [selectedNiche, setSelectedNiche] = useState('bikers');
  const [systems, setSystems] = useState({});
  const [promptSystems, setPromptSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newSourceNiche, setNewSourceNiche] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await StoryScript.getPromptSystems();
        const next = {};
        for (const item of data.items || []) {
          next[item.niche] = normalizePromptSystem(item);
        }
        if (!cancelled) {
          const items = data.items || [];
          setSystems(next);
          setPromptSystems(items);
          setSelectedNiche((current) =>
            items.some((item) => item.niche === current)
              ? current
              : items[0]?.niche || '',
          );
          setNewSourceNiche((current) =>
            current && items.some((item) => item.niche === current)
              ? current
              : items[0]?.niche || '',
          );
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.error ||
              err.message ||
              'Failed to load AI Stories prompt system',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedSystem = useMemo(
    () => normalizePromptSystem(systems[selectedNiche]),
    [systems, selectedNiche],
  );
  const chapterCount = Number(selectedSystem.chapterCount);
  const chapterCountValid = Number.isInteger(chapterCount) && chapterCount >= 1 && chapterCount <= 100;
  const minChapterWords = Number(selectedSystem.minChapterWords);
  const minChapterWordsValid = Number.isInteger(minChapterWords) && minChapterWords >= 0;

  const handleChange = (key) => (e) => {
    const value = e.target.value;
    setSystems((prev) => ({
      ...prev,
      [selectedNiche]: {
        ...normalizePromptSystem(prev[selectedNiche]),
        niche: selectedNiche,
        [key]: value,
      },
    }));
    setSuccess(false);
  };

  const syncSavedPromptSystem = (saved) => {
    const normalized = normalizePromptSystem(saved);
    setSystems((prev) => ({
      ...prev,
      [saved.niche]: normalized,
    }));
    setPromptSystems((prev) => {
      const exists = prev.some((item) => item.niche === saved.niche);
      const next = exists
        ? prev.map((item) => (item.niche === saved.niche ? normalized : item))
        : [...prev, normalized];
      return next.sort((a, b) =>
        String(a.label || a.niche).localeCompare(String(b.label || b.niche)),
      );
    });
  };

  const handleSave = async () => {
    if (!chapterCountValid) {
      setError('Chapter count must be a whole number between 1 and 100');
      return;
    }
    if (!minChapterWordsValid) {
      setError('Minimum chapter words must be a whole number of 0 or more');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const saved = await StoryScript.updatePromptSystem(selectedNiche, {
        ...selectedSystem,
        chapterCount,
        minChapterWords,
      });
      syncSavedPromptSystem(saved);
      setSuccess(true);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          'Failed to save AI Stories prompt system',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleOpenCreate = () => {
    const sourceNiche = selectedNiche || promptSystems[0]?.niche || '';
    setNewLabel('');
    setNewSourceNiche(sourceNiche);
    setError(null);
    setSuccess(false);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    const label = newLabel.trim();
    if (!label) {
      setError('Prompt system name is required');
      return;
    }
    if (!newSourceNiche) {
      setError('Choose an existing prompt system to copy');
      return;
    }
    try {
      setCreating(true);
      setError(null);
      const saved = await StoryScript.createPromptSystem({
        label,
        sourceNiche: newSourceNiche,
      });
      syncSavedPromptSystem(saved);
      setSelectedNiche(saved.niche);
      setCreateOpen(false);
      setSuccess(true);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          'Failed to create AI Stories prompt system',
      );
    } finally {
      setCreating(false);
    }
  };

  if (!AuthService.hasRole(['admin', 'superadmin'])) {
    return <Navigate to='/' replace />;
  }

  return (
    <Box>
      <Typography
        variant='h4'
        sx={{
          fontWeight: 700,
          color: 'white',
          mb: 3,
          textShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }}
      >
        Edit AI Stories Prompt System
      </Typography>

      <Paper sx={{ borderRadius: 2, background: surface.glass, boxShadow: 2 }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/ai-stories')}
              sx={{ textTransform: 'none' }}
            >
              Back to AI Stories
            </Button>
            <Button
              variant='contained'
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={loading || saving || !selectedNiche || !chapterCountValid || !minChapterWordsValid}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Save
            </Button>
            <Button
              variant='outlined'
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              disabled={loading || saving || !promptSystems.length}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              New
            </Button>
          </Box>

          {error && (
            <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity='success' sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
              Prompt system saved.
            </Alert>
          )}

          {loading ? (
            <Box display='flex' justifyContent='center' py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TextField
                select
                fullWidth
                size='small'
                label='Prompt system'
                value={selectedNiche}
                onChange={(e) => {
                  setSelectedNiche(e.target.value);
                  setSuccess(false);
                }}
                disabled={!promptSystems.length}
                helperText={!promptSystems.length ? 'No prompt systems found in the database.' : ''}
                sx={{ mb: 2 }}
              >
                {promptSystems.map((system) => (
                  <MenuItem key={system.niche} value={system.niche}>
                    {system.label || system.niche}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label='Chapter count'
                type='number'
                fullWidth
                size='small'
                value={selectedSystem.chapterCount}
                onChange={handleChange('chapterCount')}
                disabled={!selectedNiche || saving}
                error={!chapterCountValid}
                helperText={chapterCountValid
                  ? 'Applies to new outlines for this prompt system. Adjust the word targets in your prompts to change story length.'
                  : 'Enter a whole number between 1 and 100.'}
                slotProps={{ htmlInput: { min: 1, max: 100, step: 1 } }}
                sx={{ mb: 2 }}
              />

              <TextField
                select
                label='NovelCrafter-style POV'
                fullWidth
                size='small'
                value={
                  CHAPTER_POINT_OF_VIEW_OPTIONS.some(
                    (option) => option.value === selectedSystem.chapterPointOfView,
                  )
                    ? selectedSystem.chapterPointOfView
                    : 'third-person'
                }
                onChange={handleChange('chapterPointOfView')}
                disabled={!selectedNiche}
                sx={{ mb: 2 }}
              >
                {CHAPTER_POINT_OF_VIEW_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label='Story so far sent to each chapter'
                fullWidth
                size='small'
                value={
                  CHAPTER_STORY_SO_FAR_OPTIONS.some(
                    (option) => option.value === selectedSystem.chapterStorySoFar,
                  )
                    ? selectedSystem.chapterStorySoFar
                    : 'previous-chapter'
                }
                onChange={handleChange('chapterStorySoFar')}
                disabled={!selectedNiche}
                sx={{ mb: 2 }}
              >
                {CHAPTER_STORY_SO_FAR_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label='Minimum words per chapter'
                type='number'
                fullWidth
                size='small'
                value={selectedSystem.minChapterWords}
                onChange={handleChange('minChapterWords')}
                disabled={!selectedNiche || saving}
                error={!minChapterWordsValid}
                helperText={minChapterWordsValid
                  ? 'Shorter chapters are regenerated. 0 accepts any length.'
                  : 'Enter a whole number of 0 or more.'}
                slotProps={{ htmlInput: { min: 0, step: 50 } }}
                sx={{ mb: 2 }}
              />

              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Setup prompts support {'{TITLE}'}, {'{PLOT}'}, {'{CONTEXT}'}, and {'{CHAPTER_COUNT}'}. Chapter prompts support {'{storySoFar}'}, {'{pov}'}, {'{message}'}, and {'{storyNotes}'}.
              </Typography>

              {selectedNiche && FIELDS.map(({ key, label, hint }) => (
                <Accordion key={key} defaultExpanded={key === 'plotsPrompt'}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight={600}>{label}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {hint && (
                      <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                        {hint}
                      </Typography>
                    )}
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      maxRows={22}
                      value={selectedSystem[key] ?? ''}
                      onChange={handleChange(key)}
                      variant='outlined'
                      size='small'
                    />
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          )}
        </Box>
      </Paper>

      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>New prompt system</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin='dense'
            label='Prompt system'
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            fullWidth
            margin='dense'
            label='Copy from'
            value={newSourceNiche}
            onChange={(e) => setNewSourceNiche(e.target.value)}
            sx={{ mb: 2 }}
          >
            {promptSystems.map((system) => (
              <MenuItem key={system.niche} value={system.niche}>
                {system.label || system.niche}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleCreate}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={18} /> : <AddIcon />}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
